// Joins pages in one browser tab into a session, and carries the log buffers from
// one page to the next through sessionStorage (written on pagehide).

import { STORAGE_PREFIX } from '../shared/constants.js';
import { uuid, shortId } from '../shared/id.js';
import { GROUPS } from '../shared/timeline.js';

const SESSION_KEY = `${STORAGE_PREFIX}session`;
const TIMELINE_KEY = `${STORAGE_PREFIX}timeline`;
const MAX_PAGES = 20;

/** sessionStorage, or null if it's blocked (private modes, sandboxed frames). */
export function getSessionStorage() {
  try {
    const storage = window.sessionStorage;
    const key = `${STORAGE_PREFIX}test`;
    storage.setItem(key, '1');
    storage.removeItem(key);
    return storage;
  } catch {
    return null;
  }
}

/**
 * @param {Storage | null} storage
 * @param {number} [now]
 * @param {(url: string) => string} [redactUrl]
 */
export function startSession(storage, now = Date.now(), redactUrl = (url) => url) {
  let data;
  try {
    data = JSON.parse((storage && storage.getItem(SESSION_KEY)) || 'null');
  } catch {
    data = null;
  }
  if (!data || typeof data.id !== 'string' || !Array.isArray(data.pages)) {
    data = { id: uuid(), startedAt: now, pages: [] };
  }
  const page = { id: shortId(), url: redactUrl(location.href), startedAt: now };
  data.pages = [...data.pages, page].slice(-MAX_PAGES);
  try {
    if (storage) storage.setItem(SESSION_KEY, JSON.stringify(data));
  } catch {
    // quota or blocked; the session just won't span pages
  }
  return { id: data.id, startedAt: data.startedAt, page, pages: data.pages };
}

/**
 * Entries saved by the previous page in this tab, grouped like the ring buffers.
 * @param {Storage | null} storage
 * @returns {Record<string, Array<object>>}
 */
export function loadTimeline(storage) {
  if (!storage) return {};
  try {
    const data = JSON.parse(storage.getItem(TIMELINE_KEY) || 'null');
    if (!data || typeof data !== 'object') return {};
    const out = {};
    for (const group of GROUPS) {
      if (Array.isArray(data[group])) out[group] = data[group].filter((e) => e && typeof e.t === 'number');
    }
    return out;
  } catch {
    return {};
  }
}

/** Drops the bulky parts of an entry so more of them fit in storage. */
function lighten(entry) {
  const detail = entry.detail ? { ...entry.detail } : {};
  for (const part of ['request', 'response']) {
    if (detail[part] && detail[part].body) {
      detail[part] = { ...detail[part], body: undefined, note: 'Body dropped to fit session storage' };
    }
  }
  if (typeof detail.data === 'string' && detail.data.length > 200) detail.data = `${detail.data.slice(0, 200)}…`;
  if (Array.isArray(detail.args) && JSON.stringify(detail.args).length > 2000) {
    detail.args = ['[arguments dropped to fit session storage]'];
  }
  return { ...entry, detail };
}

/**
 * Saves the buffers so the next page can include them. Synchronous, so it works in pagehide.
 * Falls back to smaller versions when storage is full.
 * @param {Storage | null} storage
 * @param {Record<string, import('./ring-buffer.js').RingBuffer>} buffers
 */
export function saveTimeline(storage, buffers) {
  if (!storage) return false;
  const data = {};
  for (const group of GROUPS) data[group] = buffers[group] ? buffers[group].toArray() : [];

  const attempts = [
    () => data,
    () => mapGroups(data, (items) => items.map(lighten)),
    () => mapGroups(data, (items) => items.slice(-Math.ceil(items.length / 2)).map(lighten)),
    () => mapGroups(data, (items) => items.slice(-20).map(lighten)),
  ];
  for (const attempt of attempts) {
    try {
      storage.setItem(TIMELINE_KEY, JSON.stringify(attempt()));
      return true;
    } catch {
      // try a smaller version
    }
  }
  try {
    storage.removeItem(TIMELINE_KEY);
  } catch {
    // nothing else to do
  }
  return false;
}

function mapGroups(data, fn) {
  const out = {};
  for (const [group, items] of Object.entries(data)) out[group] = fn(items);
  return out;
}
